import React, {useCallback, useEffect, useRef, useState} from 'react';
import Spinner from "@/Components/Spinner";
import mediaFileCacheService from "@/services/MediaFileCacheService";
import {getUploadUrl} from "@/services/extractionJobManager";

interface RemoteVideoLoaderProps {
    jobId: number;
    cacheKey: string;
    fileName: string;
    onLoaded: (file: File) => void;
    onError: (message: string) => void;
    onCancel: () => void;
}

export default function RemoteVideoLoader({
    jobId,
    cacheKey,
    fileName,
    onLoaded,
    onError,
    onCancel,
}: RemoteVideoLoaderProps) {
    const [progress, setProgress] = useState(0);
    const abortControllerRef = useRef<AbortController | null>(null);

    const start = useCallback(async () => {
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const signedUrl = await getUploadUrl(jobId);
            const file = await mediaFileCacheService.downloadAndCache(
                signedUrl,
                cacheKey,
                fileName,
                setProgress,
                controller.signal
            );
            onLoaded(file);
        } catch (e: any) {
            if (e.name === 'AbortError') return;
            onError(e.message || 'Failed to load video');
        }
    }, [jobId, cacheKey, fileName]);

    useEffect(() => {
        void start();
        return () => abortControllerRef.current?.abort();
    }, []);

    const handleCancel = () => {
        abortControllerRef.current?.abort();
        onCancel();
    };

    return (
        <div className="flex flex-col items-center md:py-[11vh]">
            <Spinner size={12} progress={progress}/>
            <p className="mt-4 text-gray-600 dark:text-gray-400">
                Loading video
            </p>
            <button
                onClick={handleCancel}
                className="mt-3 py-1.5 px-3 text-sm rounded leading-normal text-gray-800 dark:text-gray-100 bg-white dark:bg-gray-700 border border-gray-100 dark:border-gray-600 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-0">
                Cancel
            </button>
        </div>
    );
}
